/* Interview Clozo — checkout wiring (CONTRACT R3 / R4; owner rulings D12.9, D17.1, D18)

   Any element carrying data-checkout starts a checkout — the plan panes, the
   sticky bar, the navbar's and every page's "Start Free Trial":
     <button|a data-checkout="monthly|yearly" data-provider="stripe"
               [data-trial] [data-intent="subscribe|renew|switch"] [data-entry="…"]>
   data-trial starts the card-required trial on that plan. An <a>'s href is the
   no-JS fallback only. PROVIDERS lists the providers this site sells through (Stripe
   only, billing v2 WP10 slice C): a press naming any other provider — a page cached
   before a provider was retired — gets PROVIDER_OFF and sends no request.

   WHO GETS WHICH ROUTE (D17.1):
     signed in  (localStorage interview_session, same rule as auth.js)
                -> POST /api/checkout (R3, the AUTHED checkout, bound to the account)
                   { plan, provider, trial?, surface:"web", intent, entry, coupon?, attribution?, timeZone, consentVersion? }
     signed out -> POST /api/public/checkout (R4) — one click to Stripe's trial
                   checkout; the account is created from the checkout email (D13.8)
                   { plan, provider, trial?, entry, coupon?, attribution?, timeZone, signinNonceHash, consentVersion? }
                   consentVersion = R31 checkoutConsentVersion (C3-K1 shown; R2031 D1 records the Affiliate Terms)
                   signinNonceHash = sha256 (hex) of a 64-hex browser nonce kept in
                   sessionStorage + localStorage "clozo_checkout_nonce" ({nonce, at});
                   success.html sends the nonce to R17 to sign this browser in (SEC-2).
   There is NO on-site checkout sheet, NO email field and NO consent checkbox
   (D12.9): the terms sit beside Stripe's pay button (R3 custom_text), which
   ends with "By continuing, you agree to our Terms of Service, Refund Policy and Affiliate Program Terms." (C3-K1);
   on the site the sentence sits only under the plan cards' pay rail
   (.pay-agreement, baked HTML; owner 2026-10-08).
   The coupon is the card's own field, else the session's applied code (coupon.js), else the link's pending code; a
   terminal coupon_* refusal clears it (ADDENDUM-COUPON-UI §5.3).

   ERRORS (the server's message is shown verbatim where it sends one):
     409 trial_already_used   signed in  -> /account.html#choose-plan (D17.1)
                              signed out -> the message + "Log in"
     409 already_entitled / already_subscribed -> the message + "Go to your account"
     409 use_in_place_action  -> the account page's in-place action (#start-plan,
                                 #switch-yearly, #pay-overdue), or window.clozoBillingUI
                                 .openAction(action) when a page provides it
     401 on R3                -> the stored session is dead: cleared, retried once on R4
   The message lands in the card's [data-checkout-error] slot when there is one,
   else in one status line fixed under the navbar, so no failure is invisible.

   Binding is ONE delegated click listener, so buttons rendered later (the
   fallback navbar, account dialogs) work, and a second copy of this script
   cannot bind twice. window.clozoCheckout.start({...}) is the programmatic entry.
   A plan pane marked data-pane-checkout is itself a Pay with Card target: a
   click anywhere on it that is not one of its own controls presses that pane's
   card button (never the trial). */
(function () {
  'use strict';
  if (window.clozoCheckout) return;   // one binding per page, whatever the markup does

  var SESSION_KEY = 'interview_session';
  var NONCE_KEY = 'clozo_checkout_nonce';
  var NONCE_TTL_MS = 2 * 60 * 60 * 1000;
  var PLANS = { monthly: 1, yearly: 1 };
  var PROVIDERS = { stripe: 1 };
  var PROVIDER_OFF = 'That payment option is no longer offered. Please pay by card.';
  var IN_PLACE = { start_plan_now: 'start-plan', switch_yearly: 'switch-yearly', pay_overdue: 'pay-overdue' };
  var inFlight = false;

  /* ---------------- session (the auth.js rule, inline: auth.js is not on every page) ---------------- */
  function sessionToken() {
    try {
      var s = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
      if (!s || !s.token) return null;
      if (typeof s.expiresAtMillis === 'number' && s.expiresAtMillis > 0 && Date.now() >= s.expiresAtMillis) return null;
      return s.token;
    } catch (_) { return null; }
  }
  function clearSession() { try { localStorage.removeItem(SESSION_KEY); } catch (_) {} }
  function timeZone() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch (_) { return undefined; }
  }

  /* ---------------- the sign-in nonce (R4 -> R17, SEC-2) ---------------- */
  function hex(bytes) {
    var s = '';
    for (var i = 0; i < bytes.length; i++) s += (bytes[i] < 16 ? '0' : '') + bytes[i].toString(16);
    return s;
  }
  function pruneNonce() {
    [localStorage, sessionStorage].forEach(function (st) {
      try {
        var r = JSON.parse(st.getItem(NONCE_KEY) || 'null');
        if (r && !(typeof r.at === 'number' && Date.now() - r.at < NONCE_TTL_MS)) st.removeItem(NONCE_KEY);
      } catch (_) { try { st.removeItem(NONCE_KEY); } catch (__) {} }
    });
  }
  /* Resolves the sha256 hex of a fresh nonce, or undefined where Web Crypto is
     missing (the buyer is then signed in by the E14 email instead — R17 404). */
  function newNonceHash() {
    try {
      if (!(window.crypto && crypto.getRandomValues && crypto.subtle && crypto.subtle.digest && window.TextEncoder)) {
        return Promise.resolve(undefined);
      }
      var raw = new Uint8Array(32);
      crypto.getRandomValues(raw);
      var nonce = hex(raw);
      var rec = JSON.stringify({ nonce: nonce, at: Date.now() });
      try { sessionStorage.setItem(NONCE_KEY, rec); } catch (_) {}
      try { localStorage.setItem(NONCE_KEY, rec); } catch (_) {}
      return crypto.subtle.digest('SHA-256', new TextEncoder().encode(nonce))
        .then(function (buf) { return hex(new Uint8Array(buf)); }, function () { return undefined; });
    } catch (_) { return Promise.resolve(undefined); }
  }

  /* ---------------- where a message goes ---------------- */
  function errorTarget(btn) {
    var card = btn && btn.closest && btn.closest('.pricing-card, .card, section');
    return (card && card.querySelector('[data-checkout-error]')) || document.getElementById('checkout-error');
  }

  /* Fill an element with a message and an optional link — or, with link.fn, a one-press button (R096 / X42) — without innerHTML. */
  function fill(el, msg, link) {
    el.textContent = msg;
    if (link) {
      el.appendChild(document.createTextNode(' '));
      var a = document.createElement(link.fn ? 'button' : 'a');
      if (link.fn) { a.type = 'button'; a.className = 'checkout-continue'; a.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); link.fn(); }); }
      else a.href = link.href;
      a.textContent = link.text;
      el.appendChild(a);
    }
  }

  /* One status line fixed under the navbar: pane-started checkouts (the pane's
     own button and error slot sit in the pay rail, usually off-screen) and any
     button without an error slot of its own (navbar, page CTAs). */
  function paneStatus(msg, isError, link) {
    var t = document.getElementById('checkoutStatus');
    if (!t) {
      t = document.createElement('div');
      t.id = 'checkoutStatus';
      t.className = 'checkout-status';
      document.body.appendChild(t);
    }
    clearTimeout(t._hide);
    t.setAttribute('role', isError ? 'alert' : 'status');
    t.classList.toggle('is-error', !!isError);
    fill(t, msg, link);
    t.hidden = false;
    if (isError) t._hide = setTimeout(function () { t.hidden = true; }, link ? 12000 : 7000);
    return t;
  }
  function hidePaneStatus() {
    var t = document.getElementById('checkoutStatus');
    if (t) { clearTimeout(t._hide); t.hidden = true; }
  }

  function showError(btn, msg, link, source) {
    var el = btn ? errorTarget(btn) : null;
    if (el) { fill(el, msg, link); el.classList.add('visible'); el.style.display = 'block'; }
    if (!el || source === 'pane') paneStatus(msg, true, link);
  }

  /* Restore the markup's OWN default, which is display:none — an inline style
     attribute on every <p data-checkout-error>. Setting '' would DELETE that
     declaration rather than restore it (styles.css also collapses
     [data-checkout-error]:empty, which covers the other callers). */
  function clearErrors() {
    document.querySelectorAll('[data-checkout-error], #checkout-error').forEach(function (e) {
      e.textContent = '';
      e.classList.remove('visible');
      e.style.display = 'none';
    });
  }

  /* The visible words of a checkout button are almost never its only child (the
     sticky bar renders "Get Monthly<svg/>", the cards "Pay with Card <span>→</span>").
     Resolve a single TEXT NODE to mutate and leave every element child untouched:
       1. an explicit <span class="btn-label">; 2. the first non-blank direct text
       node; 3. a fresh empty text node prepended to the button. */
  function labelNodeFor(btn) {
    var span = btn.querySelector('.btn-label');
    if (span) {
      if (span.firstChild && span.firstChild.nodeType === 3) return span.firstChild;
      var fresh = document.createTextNode(span.textContent || '');
      span.textContent = '';
      span.appendChild(fresh);
      return fresh;
    }
    for (var i = 0; i < btn.childNodes.length; i++) {
      var n = btn.childNodes[i];
      if (n.nodeType === 3 && n.nodeValue.trim() !== '') return n;
    }
    var created = document.createTextNode('');
    btn.insertBefore(created, btn.firstChild);
    return created;
  }

  function setBusy(btn, busy) {
    if (!btn) return;
    if (busy) {
      var label = labelNodeFor(btn);
      btn.dataset.originalLabel = label.nodeValue;
      /* One short word: "Redirecting to secure checkout…" measured 256px and the
         pay button has been as narrow as 159px. The button already names where
         it is going. */
      label.nodeValue = 'Redirecting…';
      if (btn.tagName === 'A') btn.setAttribute('aria-disabled', 'true');
      else btn.setAttribute('disabled', '');
      btn.classList.add('is-loading');
    } else {
      if (btn.dataset.originalLabel !== undefined) labelNodeFor(btn).nodeValue = btn.dataset.originalLabel;
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('is-loading');
    }
  }

  /* ---------------- entry (CONTRACT §6.2 #37 enum) ---------------- */
  function isHome() { return location.pathname === '/' || location.pathname === '/index.html'; }
  function entryFor(btn) {
    var e = btn && btn.getAttribute('data-entry');
    if (e) return e;
    if (!btn || !btn.closest) return 'page_cta';
    if (btn.closest('.sticky-cta-bar')) return 'sticky_bar';
    if (btn.closest('.navbar')) return 'nav';
    if (btn.closest('.pricing-card, [data-pane-checkout]')) return isHome() ? 'home' : 'pricing';
    return 'page_cta';
  }

  /* ---------------- attribution (click ids consent-gated; via always) ---------------- */
  /* Click ids first, then the utm tail, 16 keys max (the server's cap) — must stay in step with CLICK_KEYS in
     analytics.js. Consent is checked at SEND time: it can be withdrawn between the ad click and the checkout. The
     affiliate code is NOT consent-gated (CONTRACT-SITE §1.10, DECISIONS B1 Option A): referral.js owns it. */
  var CLICK_ONLY = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'fbc', 'msclkid', 'ttclid',
                    'twclid', 'rdt_cid', 'li_fat_id', 'epik'];
  var ATTR_KEEP = CLICK_ONLY.concat(['click_seen_at', 'utm_source', 'utm_medium', 'utm_campaign']);
  var MAX_CLICK_AGE_MS = 90 * 24 * 60 * 60 * 1000;   // Google rejects a gclid older than this
  function attributionNow() {
    try {
      var adsAllowed = !!(window.__clozoConsent &&
        typeof window.__clozoConsent.advertising === 'function' &&
        window.__clozoConsent.advertising());
      var stored = (adsAllowed && window.clozo && typeof window.clozo.attr === 'function') ? window.clozo.attr() : null;
      var picked = {}, any = false;
      if (stored) {
        /* A stale click id is worse than none: drop the click ids past 90 days but keep the utm tail. */
        var clickFresh = true;
        if (stored.click_seen_at) {
          var age = Date.now() - Date.parse(stored.click_seen_at);
          if (isFinite(age) && age > MAX_CLICK_AGE_MS) clickFresh = false;
        }
        for (var i = 0; i < ATTR_KEEP.length; i++) {
          var key = ATTR_KEEP[i];
          if (!clickFresh && (CLICK_ONLY.indexOf(key) !== -1 || key === 'click_seen_at')) continue;
          var v = stored[key];
          if (typeof v === 'string' && v) { picked[key] = v.slice(0, 200); any = true; }
        }
      }
      /* The affiliate code is a first-party functional value owned by referral.js (DECISIONS B1 Option A): it travels
         with EVERY checkout, whatever the advertising choice. The server allow-list already accepts `via`. */
      var via = null, codes = null;
      try { codes = (window.clozoReferral && typeof window.clozoReferral.codes === 'function') ? window.clozoReferral.codes() : null; via = codes && codes.last; } catch (_) {}
      if (typeof via === 'string' && via) {
        picked.via = via.slice(0, 200); any = true;
        /* PARITY-05/06: the first code and the last-click time travel too, so the server can honour attribution_model = 'first'
           and enforce cookie_days itself (a tampered localStorage never extends attribution). */
        if (codes.first && typeof codes.first === 'string') picked.via_first = codes.first.slice(0, 200);
        if (codes.lastAt && typeof codes.lastAt === 'string') picked.via_at = codes.lastAt.slice(0, 40);
      }
      return any ? picked : undefined;
    } catch (_) { return undefined; }   // attribution is never worth failing a checkout over
  }

  /* ---------------- the request ---------------- */
  function reportApi(area, path, method, r) {   // OBSERVABILITY §2.7 (site-runtime.js, W6); machine values only
    try {
      var rt = window.__clozoRuntime;
      if (!r || r.ok || !rt || typeof rt.reportFailure !== 'function') return;
      var d = r.data || {};
      rt.reportFailure(area, { path: path, method: method, status: r.status || 0, code: d.code || d.error,
        network: !!r.network, nonJson: !!r.nonJson, provider: d.provider, retryQueued: d.retryQueued });
    } catch (_) {}
    return r;
  }
  /* R2031 (D1): the checkout echoes R31 `checkoutConsentVersion` as `consentVersion` (R4 and R3) — the version of the one
     agreement sentence (C3-K1: Terms of Service, Refund Policy and Affiliate Program Terms) the plan cards and the provider's
     pay page show — so the account the checkout creates has accepted the Affiliate Program Terms. It never holds a purchase
     up: the cached config answers at once, a cold one gets 1.5 s, and no version simply sends none. */
  function consentVersion() {
    return new Promise(function (resolve) {
      var done = false;
      function fin(c) {
        if (done) return; done = true;
        var v = c && c.checkoutConsentVersion;
        resolve(typeof v === 'string' && /^[A-Za-z0-9_.-]{1,64}$/.test(v) ? v : '');
      }
      var rt = window.__clozoRuntime;
      if (!rt || typeof rt.config !== 'function') return fin(null);
      setTimeout(function () { fin(null); }, 1500);
      try { rt.config(fin); } catch (_) { fin(null); }
    });
  }

  /* FS1 #32 (FE-S1-006): the hand-off POST is aborted after CHECKOUT_TIMEOUT_MS — before, a dropped network left
     "Redirecting…" on a disabled button for ever (no AbortController, inFlight released only by a bfcache restore). */
  var CHECKOUT_TIMEOUT_MS = 12000;
  function post(path, body, token) {
    var headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = 'Bearer ' + token;
    var provider = body && body.provider;
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { try { ctrl.abort(); } catch (_) {} }, CHECKOUT_TIMEOUT_MS) : null;
    var init = { method: 'POST', headers: headers, body: JSON.stringify(body) };
    if (ctrl) init.signal = ctrl.signal;
    function rep(r) {                        // a 200 without url is a failure too (code no_url)
      var bad = !r.ok || !(r.data && r.data.url);
      if (bad) reportApi('checkout', path, 'POST', { status: r.status, ok: false, network: !!r.network, nonJson: !!r.nonJson,
        data: { code: (r.data && (r.data.code || r.data.error)) || (r.ok ? 'no_url' : undefined), provider: provider } });
      return r;
    }
    return fetch(path, init).then(function (res) {
      if (timer) clearTimeout(timer);
      var ct = res.headers.get('content-type') || '';
      if (ct.indexOf('application/json') === -1) return rep({ status: res.status, ok: false, data: {} , nonJson: true });
      return res.json().then(function (data) { return rep({ status: res.status, ok: res.ok, data: data || {} }); },
                             function () { return rep({ status: res.status, ok: false, data: {}, nonJson: true }); });
    }, function (err) {
      if (timer) clearTimeout(timer);
      var timedOut = !!(err && err.name === 'AbortError');
      reportApi('checkout', path, 'POST', { status: 0, ok: false, network: true, data: { provider: provider, code: timedOut ? 'timeout' : undefined } });
      if (timedOut) { var e = new Error('timeout'); e.timedOut = true; throw e; }
      throw err;
    });
  }

  function humanMessage(r) {
    var d = r.data || {};
    var code = d.code || d.error || '';
    if (typeof d.message === 'string' && d.message) return d.message;
    if (r.status === 429) return 'Too many attempts from this connection. Wait a minute and try again. Nothing was charged.';
    if (r.nonJson || r.status >= 500) return 'Checkout is temporarily unavailable — nothing was charged. Please try again in a moment.';
    // A legacy body whose `error` is a sentence rather than a machine code.
    if (typeof d.error === 'string' && /\s/.test(d.error)) return d.error;
    return 'Could not start checkout — nothing was charged. Please try again.';
  }

  /* cfg: {plan, provider, trial, intent, entry, coupon, source}; btn: the pressed element or null. */
  function go(cfg, btn) {
    if (inFlight) return Promise.resolve(false);
    if (PLANS[cfg.plan] && !PROVIDERS[cfg.provider]) {   // a cached page's button for a retired provider: no request
      clearErrors();
      showError(btn, PROVIDER_OFF, null, cfg.source);
      return Promise.resolve(false);
    }
    if (!PLANS[cfg.plan] || !PROVIDERS[cfg.provider]) return Promise.resolve(false);
    clearErrors();
    /* The applied code (coupon.js, ADDENDUM-COUPON-UI D-CU-14) rides on every checkout that has no code of its own: the
       sticky bar, the navbar, page CTAs and clozoCheckout.start() callers have no field in scope. A value typed into a card's
       own field still wins for that card's buttons (cfgFromButton). */
    if (!cfg.coupon && !cfg.noCoupon) { try { var applied = window.clozoCoupon && window.clozoCoupon.code(); if (applied) cfg.coupon = applied; } catch (_) {} }
    /* Fix (CPN-UX-04): a "20% off" link must not lose its discount to a fast click or a failed validate — when nothing is applied yet, the
       link's code (this load's ?via, else the 90-day store) rides as a PENDING code; the server resolves it like any code. */
    if (!cfg.coupon && !cfg.noCoupon) { try { var pend = window.clozoCoupon && window.clozoCoupon.pending(); if (pend) { cfg.coupon = pend; cfg.couponPending = true; } } catch (_) {} }

    // checkout_start (PostHog/pixels, consent-gated in analytics.js) BEFORE the fetch,
    // so a visitor who reaches the payment decision is counted even if the call fails.
    try {
      if (window.clozo && typeof window.clozo.track === 'function') {
        window.clozo.track('checkout_start', { plan: cfg.plan, provider: cfg.provider, source: cfg.source, trial: !!cfg.trial, entry: cfg.entry });
      }
    } catch (_) { /* analytics must never block a purchase */ }
    // #37 checkout_started — first-party, always on (D13.3); site-runtime.js owns the queue.
    try {
      var rt = window.__clozoRuntime;
      if (rt && typeof rt.track === 'function') {
        rt.track('checkout_started', { planID: cfg.plan, provider: cfg.provider, trial: !!cfg.trial, entry: cfg.entry });
      }
    } catch (_) {}

    inFlight = true;
    var pane = btn && btn.closest ? btn.closest('[data-pane-checkout]') : null;
    if (pane) pane.classList.add('is-checking-out');
    if (cfg.source === 'pane') paneStatus('Opening secure card checkout…');
    else hidePaneStatus();
    setBusy(btn, true);

    var attribution = attributionNow();
    var tz = timeZone();

    function fail(msg, link) {
      inFlight = false;
      if (pane) pane.classList.remove('is-checking-out');
      setBusy(btn, false);
      showError(btn, msg, link, cfg.source);
      return false;
    }
    function leave(url) {
      try {
        var to = new URL(url, location.href);
        if (to.origin === location.origin && to.pathname === location.pathname && to.search === location.search && to.hash) {
          // Same page (e.g. already on account.html): only the hash changes, nothing navigates — release the button.
          inFlight = false;
          if (pane) pane.classList.remove('is-checking-out');
          setBusy(btn, false);
          hidePaneStatus();
          location.hash = to.hash;
          return false;
        }
      } catch (_) {}
      window.location.href = url;
      return true;
    }

    function signedOut() {
      return Promise.all([newNonceHash(), consentVersion()]).then(function (got) {
        var nonceHash = got[0], cv = got[1];
        var body = { plan: cfg.plan, provider: cfg.provider, entry: cfg.entry };
        if (cfg.trial) body.trial = true;
        if (cfg.coupon) body.coupon = cfg.coupon;
        if (attribution) body.attribution = attribution;
        if (tz) body.timeZone = tz;
        if (nonceHash) body.signinNonceHash = nonceHash;
        if (cv) body.consentVersion = cv;
        return post('/api/public/checkout', body, null);
      }).then(function (r) { return handle(r, false); });
    }

    function signedIn(token) {
      var body = { plan: cfg.plan, provider: cfg.provider, surface: 'web', intent: cfg.intent, entry: cfg.entry };
      if (cfg.trial) body.trial = true;
      if (cfg.coupon) body.coupon = cfg.coupon;
      if (attribution) body.attribution = attribution;
      if (tz) body.timeZone = tz;
      function send(b) {
        return post('/api/checkout', b, token).then(function (r) {
          if (r.status === 401) { clearSession(); return signedOut(); }   // a dead session: once, as a signed-out buyer
          /* a version newer than the cached site-config: once more without the echo (never a silent consent to unseen words) */
          if (r.status === 409 && b.consentVersion && ((r.data && (r.data.code || r.data.error)) === 'disclosure_changed')) {
            var plain = {}; for (var k in b) if (k !== 'consentVersion') plain[k] = b[k];
            return send(plain);
          }
          return handle(r, true);
        });
      }
      return consentVersion().then(function (cv) { if (cv) body.consentVersion = cv; return send(body); });
    }

    /* R096 / X42: one press continues at the regular price — the code is removed and the same checkout starts again, once */
    function withoutCode() {
      try { if (window.clozoCoupon) window.clozoCoupon.clear('checkout:continue'); } catch (_) {}
      var plain = { noCoupon: true }; for (var k in cfg) if (k !== 'coupon' && k !== 'couponPending') plain[k] = cfg[k];
      hidePaneStatus(); clearErrors();
      return go(plain, btn);
    }
    function handle(r, authed) {
      var d = r.data || {};
      if (r.ok && typeof d.url === 'string' && d.url) return leave(d.url);
      var code = d.code || d.error || '';
      if (r.status === 409 && code === 'trial_already_used') {
        if (authed) return leave('/account.html#choose-plan');
        return fail(humanMessage(r), { href: '/login.html?next=' + encodeURIComponent('/account.html'), text: 'Log in' });
      }
      if (r.status === 409 && (code === 'already_entitled' || code === 'already_subscribed')) {
        return fail(humanMessage(r), { href: '/account.html', text: 'Go to your account' });
      }
      if (r.status === 409 && code === 'use_in_place_action') {
        var ui = window.clozoBillingUI;
        if (ui && typeof ui.openAction === 'function') {
          var opened = false;
          try { opened = ui.openAction(d.action, { plan: cfg.plan, provider: cfg.provider }) === true; } catch (_) {}
          if (opened) {
            inFlight = false;
            if (pane) pane.classList.remove('is-checking-out');
            setBusy(btn, false);
            hidePaneStatus();
            return false;
          }
        }
        return leave('/account.html' + (IN_PLACE[d.action] ? '#' + IN_PLACE[d.action] : ''));
      }
      if (/^coupon_/.test(code)) {   // CONTRACT-API §3.10 / §3.8 (Fix CPN-UX-28, CPN-UX-04)
        var TERMINAL = /^coupon_(invalid|expired|exhausted|disabled|self|already_used|inactive)$/;
        if (cfg.couponPending) {     // a code the visitor never saw applied: never block the sale, never show a message — drop it and go on at the regular price
          try { if (window.clozoCoupon) window.clozoCoupon.clear('pending:' + code); } catch (_) {}
          var plain = {}; for (var k in cfg) if (k !== 'coupon' && k !== 'couponPending') plain[k] = cfg[k];
          inFlight = false; if (pane) pane.classList.remove('is-checking-out'); setBusy(btn, false);   // the release fail() does, without a message
          return go(plain, btn);     // ONE retry without the code (clear() made pending() null and the retried cfg has no couponPending — it can never loop)
        }
        if (TERMINAL.test(code)) {   // the code itself is refused: its sentence, then ONE press continues at the regular price (X42)
          return fail(humanMessage(r), { text: 'Continue at the regular price', fn: withoutCode });
        }
        return fail(humanMessage(r));   // coupon_provider_unavailable / coupon_unavailable: the code STAYS applied; the server sentence says what to do
      }
      /* R096: a checkout carrying a code that fails for any other reason never dead-ends — offer the same checkout without it */
      if (cfg.coupon && (r.status >= 500 || r.nonJson || r.status === 400)) return fail(humanMessage(r), { text: 'Continue without the code', fn: withoutCode });
      return fail(humanMessage(r));
    }

    var token = sessionToken();
    var run = token ? signedIn(token) : signedOut();
    return run.catch(function (err) {
      if (err && err.timedOut) return fail('Still connecting — nothing was charged. Try again; if your company network blocks Stripe, try from a personal connection.');
      return fail('Could not reach checkout — nothing was charged. Check your connection and try again.');
    });
  }

  function cfgFromButton(btn) {
    var trial = btn.hasAttribute('data-trial');
    var card = btn.closest('.pricing-card, .card, section');
    var couponInput = card && card.querySelector('[data-coupon-input]');
    var coupon = couponInput && couponInput.value.trim() ? couponInput.value.trim() : undefined;
    var source = btn.dataset.via || 'button';   // 'pane' when the click came from the plan pane (below)
    delete btn.dataset.via;
    return {
      plan: btn.getAttribute('data-checkout'),
      provider: btn.getAttribute('data-provider') || 'stripe',
      trial: trial,
      intent: trial ? 'trial' : (btn.getAttribute('data-intent') || 'subscribe'),
      entry: entryFor(btn),
      coupon: coupon,
      source: source
    };
  }

  /* ---------------- one delegated listener ---------------- */
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-checkout]') : null;
    if (!btn) return;
    if (btn.tagName === 'A') {
      // A modified or non-primary click keeps the browser's own behaviour (new tab on the fallback href).
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
    }
    if (btn.hasAttribute('disabled') || btn.getAttribute('aria-disabled') === 'true' || btn.classList.contains('is-loading')) return;
    go(cfgFromButton(btn), btn);
  });

  /* The whole pane is the Pay with Card button (owner, 2026-09-25): the pay rail
     sits at the foot of an eleven-line feature list. Delegating to the pane's OWN
     card button keeps one code path (coupon, "Redirecting…", the error slot,
     analytics, the in-flight guard).
     What the pane leaves alone, so it never starts the WRONG checkout:
       · every control inside it (the trial, the coupon box, any link,
         the agreement line) and the error message;
       · the pay rail and everything below its top edge;
       · a press that STARTED on a control, or moved, before it was released;
       · any checkout already in flight anywhere on the page;
       · a drag or shift-click that selected text in the pane. */
  var PANE_IGNORE = 'button, a, input, select, textarea, label, summary, [contenteditable], [data-checkout-error], .pay-actions, .pay-code';
  // Mouse: 8px is a drag (a text selection). Touch and pen: a finger wobbles more.
  var paneSlop = function (type) { return type === 'mouse' ? 8 : 16; };
  var selectionIn = function (el) {
    var sel = window.getSelection && window.getSelection();
    return !!(sel && !sel.isCollapsed && sel.anchorNode && el.contains(sel.anchorNode));
  };
  /* OQ-FS1-5 (FS1 #33, FE-S1-014): what a click on the pane's body starts. 'card' = the owner's 2026-09-25 ruling
     (Pay with Card). 'trial' = the prepared alternative: for a SIGNED-OUT visitor the pane presses its own Start Free
     Trial button (the intent every CTA that led here stated); signed in, the card button as today. One word to flip. */
  var PANE_CLICK_STARTS = 'card';
  function paneButton(pane) {
    var cardBtn = pane.querySelector('[data-checkout][data-provider="stripe"]:not([data-trial])');
    if (PANE_CLICK_STARTS !== 'trial' || sessionToken()) return cardBtn;
    return pane.querySelector('[data-checkout][data-provider="stripe"][data-trial]') || cardBtn;
  }
  function bindPanes() {
    document.querySelectorAll('[data-pane-checkout]').forEach(function (pane) {
      if (pane.dataset.paneBound) return;
      var cardBtn = pane.querySelector('[data-checkout][data-provider="stripe"]:not([data-trial])');
      if (!cardBtn) return;
      pane.dataset.paneBound = '1';
      var rail = pane.querySelector('.pay-actions');
      var down = null;
      pane.addEventListener('pointerdown', function (e) {
        down = { target: e.target, x: e.clientX, y: e.clientY, type: e.pointerType, hadSelection: selectionIn(pane) };
      });
      pane.addEventListener('click', function (e) {
        var press = down;
        down = null;
        if (e.defaultPrevented || e.button !== 0) return;
        if (e.target.closest && e.target.closest(PANE_IGNORE)) return;
        if (press) {
          if (press.target.closest && press.target.closest(PANE_IGNORE)) return;
          var slop = paneSlop(press.type);
          if (Math.abs(e.clientX - press.x) > slop || Math.abs(e.clientY - press.y) > slop) return;
        }
        if (rail && e.clientY >= rail.getBoundingClientRect().top) return;
        // A selection THIS press made is not a purchase; one that was already there is being dismissed.
        if (selectionIn(pane) && !(press && press.hadSelection)) return;
        if (inFlight || document.querySelector('[data-checkout][disabled], [data-checkout].is-loading')) return;
        var target = paneButton(pane) || cardBtn;
        target.dataset.via = 'pane';
        target.click();
      });
    });
  }

  /* Checkout closed on Stripe: R4's cancel_url is pricing.html?checkout=canceled,
     R3's is account.html?checkout=canceled. Say so for 5 s, then drop the parameter. */
  function canceledNotice() {
    try {
      var u = new URL(location.href);
      if (u.searchParams.get('checkout') !== 'canceled') return;
      var t = paneStatus('Checkout closed — nothing was charged.', false);
      t._hide = setTimeout(function () { t.hidden = true; }, 5000);
      u.searchParams.delete('checkout');
      history.replaceState(history.state, '', u.pathname + (u.search || '') + u.hash);
    } catch (_) {}
  }

  function onReady() { bindPanes(); canceledNotice(); pruneNonce(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady);
  else onReady();

  /* bfcache restore: after a successful checkout the button is left busy while the
     browser navigates away. Coming BACK restores the page exactly as it was —
     busy and mislabeled, with no way to retry. Reset everything on restore. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    inFlight = false;
    hidePaneStatus();
    document.querySelectorAll('.is-checking-out').forEach(function (p) { p.classList.remove('is-checking-out'); });
    document.querySelectorAll('[data-checkout]').forEach(function (btn) {
      if (btn.hasAttribute('disabled') || btn.getAttribute('aria-disabled') === 'true' || btn.classList.contains('is-loading')) setBusy(btn, false);
    });
  });

  window.clozoCheckout = {
    /* opts: {plan, provider?, trial?, intent?, entry?, coupon?, el?} — the same flow as a click. */
    start: function (opts) {
      opts = opts || {};
      var trial = !!opts.trial;
      return go({
        plan: opts.plan,
        provider: opts.provider || 'stripe',
        trial: trial,
        intent: trial ? 'trial' : (opts.intent || 'subscribe'),
        entry: opts.entry || entryFor(opts.el || null),
        coupon: opts.coupon,
        source: 'api'
      }, opts.el || null);
    },
    isSignedIn: function () { return !!sessionToken(); }
  };
})();
